import { AlertCircle, Check, FileText, Upload, UploadCloud, X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shared/ui/dialog'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Spinner } from '@/shared/ui/spinner'
import { Textarea } from '@/shared/ui/textarea'
import { formatBytes } from '@/shared/utils/handleData'
import {
  mergeSelection,
  queueProgress,
  splitByMaxSize,
  UPLOAD_ITEM_STATUS,
  type UploadQueueItem,
  uploadItemKey
} from '../domain/upload-queue'
import { CATALOG_OWNER_LABEL, type CatalogOwnerType } from '../models/document-owner'

/** 30 MB, el mismo tope que valida el backend. Repetirlo acá evita un viaje para nada. */
const MAX_UPLOAD_BYTES = 30 * 1024 * 1024

type UploadFormValues = {
  name: string
  comment: string
  /** `''` = sin clasificar. El `<select>` no puede llevar `null` como valor. */
  categoryId: string
  /** Publicar lo subido en el mismo acto. Arranca SIEMPRE apagado. */
  publish: boolean
}

/** El valor del ítem «sin categoría». No puede ser `''`: Radix lo reserva para limpiar. */
const NO_CATEGORY = '__none__'

/**
 * Para qué se abrió el diálogo.
 *
 * No es cosmético. En `photo` el archivo va al carrusel que la app pinta en la portada, y ahí la
 * categoría —el eje con el que el cliente agrupa su cartapacio— no tiene dónde aplicarse: la
 * galería es una tira ordenada, no una lista agrupada. Pedir un dato que nadie va a leer es
 * pedirle al curador que adivine cuál da igual.
 */
type UploadMode = 'document' | 'photo'

export type DocumentUploadDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  ownerType: CatalogOwnerType
  pending: boolean
  /** La plantilla global, ya sin las deprecadas. Vacía mientras carga. */
  categories: Array<{ id: string; name: string }>
  onSubmit: (values: {
    files: File[]
    name?: string
    comment?: string
    categoryId?: string
    publish?: boolean
  }) => void
  /** Cuántos archivos admite de una. Más de uno habilita la selección múltiple. */
  maxFiles?: number
  /** `document` (por defecto) sube al cartapacio; `photo` sube a la galería. */
  mode?: UploadMode
  /** Si quien mira tiene `PublishCatalogDocuments`. Sin el permiso la casilla NI SE MUESTRA. */
  canPublish?: boolean
  /** A quién le llega lo que se publica en este nivel. Se lee ANTES de marcar, no después. */
  publishReach?: string
  /**
   * El estado de CADA archivo del lote en curso. Lo lleva quien sube, porque es quien recorre la
   * cola; acá sólo se dibuja.
   */
  queue?: UploadQueueItem[]
}

/**
 * SUBIR AL CARTAPACIO: se arrastra o se elige, y se ve archivo por archivo cómo va.
 *
 * ## El dropzone no es un adorno: era un `<input type="file">` ESCONDIDO
 *
 * Lo único que había era un botón que abría el picker. Arrastrar una carpeta desde el escritorio
 * —que es cómo se cargan doce planos— no hacía nada, ni siquiera avisaba. Ahora el área entera
 * recibe, y el picker sigue estando para quien prefiere elegir.
 *
 * ☠️ **Soltar AGREGA, el picker REEMPLAZA.** No es una inconsistencia que se pueda pulir: el
 * `<input type="file">` reemplaza su selección por definición y eso no se puede cambiar. Por eso
 * la acumulación vive en `mergeSelection` y las dos puertas entran por ahí — soltar una segunda
 * tanda suma a la primera, que es lo que quien arrastra espera.
 *
 * ## El progreso es por ARCHIVO, y no hay barra de bytes
 *
 * Las subidas son secuenciales a propósito (el orden de la galería sale del orden en que
 * terminan), así que veinte archivos son varios minutos. Antes eso era un spinner: no se sabía si
 * iba por el segundo o por el decimoctavo, ni cuál de los veinte había fallado.
 *
 * Lo que NO se hace es una barra por bytes. La subida viaja por el cliente generado del contrato
 * (`fetch`), y `fetch` no publica progreso de subida: una barra animada sin datos detrás promete
 * una precisión que no existe y se queda quieta justo cuando el archivo grande se toma su tiempo.
 * Lo que sí es verdad —en cuál va, cuáles entraron, cuál falló y por qué— es lo que se muestra.
 *
 * ## Un archivo que falla no cancela el lote
 *
 * Los otros ya se eligieron y no hay motivo para tirarlos. Su fila queda en rojo con el mensaje
 * del servidor al lado, que es donde se puede leer — un toast que se va no dice cuál de los ocho.
 */
export function DocumentUploadDialog({
  open,
  onOpenChange,
  ownerType,
  pending,
  categories,
  onSubmit,
  mode = 'document',
  maxFiles = 1,
  canPublish = false,
  publishReach,
  queue = []
}: DocumentUploadDialogProps) {
  const isPhoto = mode === 'photo'
  const multiple = maxFiles > 1
  const [files, setFiles] = useState<File[]>([])
  const [fileError, setFileError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  /**
   * `dragenter`/`dragleave` disparan por CADA descendiente que el puntero cruza, así que un
   * contador de booleano prende y apaga el resaltado mientras se pasea por dentro de la zona.
   * Contando entradas y salidas, el resaltado sólo se apaga cuando se salió de verdad.
   */
  const dragDepth = useRef(0)
  const fileInputId = useId()
  const nameId = useId()
  const commentId = useId()
  const publishId = useId()
  const categoryId = useId()

  const { control, handleSubmit, reset } = useForm<UploadFormValues>({
    defaultValues: { name: '', comment: '', categoryId: NO_CATEGORY, publish: false }
  })

  /**
   * ☠️ EL RESET VA AL **ABRIR**, NO AL CERRAR.
   *
   * Al cerrar no alcanza: los consumidores cierran el diálogo con su propio `setUploadOpen(false)`
   * desde el `onSuccess`, y ese camino NO pasa por `onOpenChange` —a ése lo dispara Radix cuando
   * cierra el usuario, no cuando cierra el padre—. O sea que justo el cierre más frecuente, el
   * del final feliz, era el único que no limpiaba nada: la segunda foto se abría con la primera
   * todavía elegida, y darle «Subir» de nuevo la subía DOS VECES.
   */
  useEffect(() => {
    if (!open) return
    reset({ name: '', comment: '', categoryId: NO_CATEGORY, publish: false })
    setFiles([])
    setFileError(null)
    setDragging(false)
    dragDepth.current = 0
    // El `<input type="file">` se guarda su propio valor y no lo limpia ningún reset de React.
    // Sin esta línea, volver a elegir EL MISMO archivo no dispara `change` —el valor no cambió— y
    // el diálogo se queda sin archivo sin decir por qué.
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [open, reset])

  /**
   * La única puerta de entrada de archivos: la usan el picker y el soltar.
   *
   * `replace` distingue las dos: el picker reemplaza porque es lo que el control hace, soltar
   * acumula porque es lo que quien arrastra espera.
   */
  const acceptFiles = (picked: FileList | null, replace: boolean) => {
    const chosen = Array.from(picked ?? [])
    if (chosen.length === 0) return

    // La galería sólo acepta imágenes. Al SOLTAR hay que filtrarlo acá: `accept` sólo gobierna el
    // picker, y un PDF arrastrado a la galería subiría bien al cartapacio para fallar recién en el
    // paso al slot, dejando un archivo suelto que nadie pidió.
    const typed = isPhoto ? chosen.filter((file) => file.type.startsWith('image/')) : chosen
    if (typed.length === 0) {
      setFileError('La galería sólo acepta imágenes.')
      return
    }

    const merged = replace ? typed : mergeSelection(files, typed)

    // Se avisa acá y no después del 413: el archivo grande tarda en subir justo para volver
    // rechazado. Con varios se NOMBRAN los que sobran en vez de decir «alguno es muy grande»: con
    // ocho seleccionados, saber cuál es la mitad del arreglo.
    const { accepted, rejected } = splitByMaxSize(merged, MAX_UPLOAD_BYTES)
    if (rejected.length > 0) {
      setFileError(
        `${rejected.map((item) => item.name).join(', ')} ${rejected.length === 1 ? 'supera' : 'superan'} el máximo de ${formatBytes(MAX_UPLOAD_BYTES, 0)}.`
      )
    } else if (accepted.length > maxFiles) {
      // El tope se comprueba contra lo que QUEDA libre, no contra el total: la galería puede
      // tener nueve puestas y admitir sólo tres más.
      setFileError(
        maxFiles === 1
          ? 'Solo se puede subir un archivo a la vez.'
          : `Puedes subir hasta ${maxFiles} de una vez. Llevas ${accepted.length}.`
      )
    } else {
      setFileError(null)
    }

    setFiles(accepted.slice(0, Math.max(maxFiles, 1)))
  }

  /**
   * Saca UN archivo del lote sin deshacer la selección entera.
   *
   * Existe porque el picker no sabe editar: volver a abrirlo REEMPLAZA la selección, así que sin
   * esto, colar un archivo de más entre ocho obliga a elegir los siete buenos otra vez.
   */
  const dropFile = (key: string) => {
    setFiles((current) => current.filter((file) => uploadItemKey(file) !== key))
    setFileError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const progress = queueProgress(queue)
  const uploading = pending && queue.length > 0
  const statusByKey = new Map(queue.map((item) => [item.key, item]))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        ☠️ `max-h` + el cuerpo que scrollea, y el pie FUERA de ese scroll.
       
        El diálogo creció con el dropzone y la cola de archivos, y con ocho seleccionados pasa de
        largo la altura de una pantalla de portátil. Sin esto el pie —donde está «Subir»— queda
        por debajo del borde de la ventana y **no hay forma de llegar**: el diálogo no scrollea,
        la página de atrás está bloqueada, y el botón existe pero no se puede apretar. Lo destapó
        el E2E, que resolvió el botón y se quedó esperando a que fuera accionable.
      */}
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 sm:max-w-md">
        <DialogHeader className="shrink-0 pb-4">
          <DialogTitle>{isPhoto ? 'Subir fotos' : 'Subir documentos'}</DialogTitle>
          <DialogDescription>
            {isPhoto ? (
              <>
                Se agregan a la galería de {CATALOG_OWNER_LABEL[ownerType]} y quedan{' '}
                <strong>internas</strong>. Para que los clientes las vean en la app tienes que
                publicarlas después, con el botón de cada foto.
              </>
            ) : (
              <>
                Se guardan en {CATALOG_OWNER_LABEL[ownerType]} y quedan <strong>internos</strong>.
                Para que los clientes los vean tienes que publicarlos después, desde la lista.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={handleSubmit((values) => {
            if (files.length === 0) {
              setFileError(multiple ? 'Elige al menos un archivo.' : 'Elige un archivo para subir.')
              return
            }
            onSubmit({
              files,
              name: values.name.trim() || undefined,
              comment: values.comment.trim() || undefined,
              categoryId: values.categoryId === NO_CATEGORY ? undefined : values.categoryId,
              // Sin el permiso NO viaja, aunque el valor del form dijera otra cosa: la casilla
              // está escondida, no deshabilitada, y un form que manda lo que no muestra miente.
              publish: canPublish && values.publish
            })
          })}
        >
          {/* El CUERPO scrollea; el pie no. Así «Subir» está siempre a un click. */}
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
            <Field data-invalid={Boolean(fileError)}>
              <FieldLabel htmlFor={fileInputId}>{multiple ? 'Archivos' : 'Archivo'}</FieldLabel>

              <input
                ref={fileInputRef}
                id={fileInputId}
                type="file"
                className="sr-only"
                accept={isPhoto ? 'image/*' : undefined}
                multiple={multiple}
                onChange={(event) => {
                  acceptFiles(event.target.files, true)
                }}
              />

              {/*
               * El dropzone. Es un `<button>` de verdad y no un `div` con `onClick`: así lo alcanza
               * el tabulador y la barra espaciadora lo abre, que es lo único que tiene quien no usa
               * el mouse. Arrastrar es un atajo ENCIMA de esa acción, nunca la única forma.
               */}
              <button
                type="button"
                disabled={pending}
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={(event) => {
                  event.preventDefault()
                  dragDepth.current += 1
                  setDragging(true)
                }}
                onDragOver={(event) => {
                  // Sin esto el navegador ABRE el archivo en la pestaña y se pierde el trabajo:
                  // `dragover` tiene que cancelarse en CADA evento, no alcanza con `dragenter`.
                  event.preventDefault()
                }}
                onDragLeave={(event) => {
                  event.preventDefault()
                  dragDepth.current -= 1
                  if (dragDepth.current <= 0) setDragging(false)
                }}
                onDrop={(event) => {
                  event.preventDefault()
                  dragDepth.current = 0
                  setDragging(false)
                  acceptFiles(event.dataTransfer.files, false)
                }}
                className={cn(
                  'flex w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-input px-3 py-6 text-center transition-colors duration-150',
                  'hover:border-ring/60 hover:bg-muted/40',
                  'focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none',
                  dragging && 'border-ring bg-accent',
                  pending && 'pointer-events-none opacity-60'
                )}
              >
                <UploadCloud
                  aria-hidden
                  className={cn('h-5 w-5 text-muted-foreground', dragging && 'text-foreground')}
                />
                <span className="text-xs font-medium">
                  {dragging
                    ? 'Suelta para agregar'
                    : multiple
                      ? 'Arrastra tus archivos o haz clic para elegirlos'
                      : 'Arrastra el archivo o haz clic para elegirlo'}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {isPhoto ? 'Imágenes' : 'PDF, imágenes y documentos'} de hasta{' '}
                  {formatBytes(MAX_UPLOAD_BYTES, 0)}
                  {multiple ? ` · hasta ${maxFiles}` : ''}
                </span>
              </button>

              {/*
               * La barra general aparece SÓLO durante la subida. Mide archivos resueltos sobre el
               * total —lo que se sabe con certeza— y no bytes, que `fetch` no publica.
               */}
              {uploading ? (
                <div
                  className="mt-1 flex flex-col gap-1"
                  role="status"
                  // `polite` y no `assertive`: es un avance esperado, no una alarma. Interrumpiría
                  // al lector de pantalla en cada archivo de un lote de veinte.
                  aria-live="polite"
                >
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span className="min-w-0 truncate">
                      {progress.current ? `Subiendo ${progress.current}…` : 'Terminando…'}
                    </span>
                    <span className="shrink-0 tabular-nums">
                      {progress.settled} / {progress.total}
                    </span>
                  </div>
                  <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary transition-[width] duration-200 ease-out"
                      style={{ width: `${progress.percent}%` }}
                    />
                  </div>
                </div>
              ) : null}

              {files.length > 0 ? (
                <ul className="mt-1 flex max-h-52 flex-col gap-1 overflow-y-auto">
                  {files.map((file) => {
                    // La key NO lleva el índice: quitar un archivo del medio corre a todos los de
                    // abajo, y una key posicional haría que React reutilice el nodo equivocado.
                    const key = uploadItemKey(file)
                    const status = statusByKey.get(key)
                    const failed = status?.status === UPLOAD_ITEM_STATUS.failed
                    const done = status?.status === UPLOAD_ITEM_STATUS.done
                    const active = status?.status === UPLOAD_ITEM_STATUS.uploading

                    return (
                      <li
                        key={key}
                        className={cn(
                          'flex items-center gap-2 rounded-md border px-2 py-1.5',
                          failed ? 'border-destructive/40 bg-destructive/5' : 'border-border'
                        )}
                      >
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
                          {active ? (
                            <Spinner className="h-3.5 w-3.5" />
                          ) : done ? (
                            <Check className="h-3.5 w-3.5 text-emerald-600" />
                          ) : failed ? (
                            <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                          ) : (
                            <FileText className="h-3.5 w-3.5" />
                          )}
                        </span>

                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-xs font-medium" title={file.name}>
                            {file.name}
                          </span>
                          <span
                            className={cn(
                              'truncate text-[11px]',
                              failed ? 'text-destructive' : 'text-muted-foreground'
                            )}
                          >
                            {/* El error del SERVIDOR, en la fila del archivo que lo produjo. Un
                              toast diría «falló uno» y se iría antes de decir cuál. */}
                            {failed
                              ? (status?.error ?? 'No se pudo subir.')
                              : status?.unpublished
                                ? 'Subido, sin publicar'
                                : formatBytes(file.size)}
                          </span>
                        </span>

                        {pending ? null : (
                          <Button
                            type="button"
                            size="icon-xs"
                            variant="ghost"
                            aria-label={`Quitar ${file.name} de la selección`}
                            onClick={() => {
                              dropFile(key)
                            }}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </li>
                    )
                  })}
                </ul>
              ) : null}

              {files.length > 1 ? (
                <FieldDescription>
                  {formatBytes(files.reduce((total, item) => total + item.size, 0))} en total
                </FieldDescription>
              ) : null}

              <FieldError errors={fileError ? [{ message: fileError }] : []} />
            </Field>

            {/* Un nombre visible es de UN archivo. Con varios el campo se esconde: aplicarlo a
              todos dejaría cinco filas llamadas igual —peor que el nombre del archivo— y ofrecer
              uno por archivo convertiría subir diez fotos en diez campos. Con uno solo SÍ está,
              porque renombrar después ya se puede, pero hacerlo al subir ahorra el viaje. */}
            {files.length > 1 ? null : (
              <Controller
                control={control}
                name="name"
                rules={{ maxLength: { value: 200, message: 'Máximo 200 caracteres.' } }}
                render={({ field, fieldState }) => (
                  <Field data-invalid={Boolean(fieldState.error)}>
                    <FieldLabel htmlFor={nameId}>Nombre visible</FieldLabel>
                    <Input
                      id={nameId}
                      placeholder={files[0]?.name ?? 'Como lo verá quien lo lea'}
                      {...field}
                    />
                    <FieldDescription>
                      Si lo dejas vacío se usa el nombre del archivo. Puedes cambiarlo después.
                    </FieldDescription>
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />
            )}

            {isPhoto ? null : (
              <Controller
                control={control}
                name="categoryId"
                render={({ field, fieldState }) => (
                  <Field data-invalid={Boolean(fieldState.error)}>
                    <FieldLabel htmlFor={categoryId}>Categoría</FieldLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger id={categoryId}>
                        <SelectValue placeholder="Elige una categoría" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* No clasificar es una elección válida, y esconderla haría que quien no
                          sabe dónde va el archivo abandone la subida. */}
                        <SelectItem value={NO_CATEGORY}>Sin categoría</SelectItem>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FieldDescription>
                      Es el grupo con el que el cliente lo ve en su app. Sin categoría aparece en
                      «Otros documentos», y puedes cambiarlo después.
                    </FieldDescription>
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />
            )}

            <Controller
              control={control}
              name="comment"
              rules={{ maxLength: { value: 1000, message: 'Máximo 1000 caracteres.' } }}
              render={({ field, fieldState }) => (
                <Field data-invalid={Boolean(fieldState.error)}>
                  <FieldLabel htmlFor={commentId}>Comentario</FieldLabel>
                  <Textarea id={commentId} rows={2} {...field} />
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />

            {/*
             * ── LA CASILLA DE PUBLICAR ───────────────────────────────────────────────
             *
             * Se sostienen las tres cosas que hacen que el permiso siga valiendo:
             * 1. Sin `PublishCatalogDocuments` la casilla NO se renderiza, y aun así el backend
             *    autoriza cada publicación por separado: forzar el valor no publica nada.
             * 2. Subir y publicar siguen siendo dos actos. La subida nace interna —el endpoint no
             *    acepta visibilidad— y publicar es una llamada aparte. Si falla, el archivo queda
             *    subido e interno, que es el estado seguro, y la fila lo dice.
             * 3. Arranca APAGADA en cada apertura, y el alcance se lee acá mismo antes de marcarla.
             */}
            {canPublish ? (
              <Controller
                control={control}
                name="publish"
                render={({ field }) => (
                  <Field
                    orientation="horizontal"
                    className="rounded-md border border-amber-200 bg-amber-50 p-2.5 dark:border-amber-900/60 dark:bg-amber-950/30"
                  >
                    <Checkbox
                      id={publishId}
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked === true)
                      }}
                    />
                    <div className="flex flex-col gap-0.5">
                      <FieldLabel htmlFor={publishId} className="font-medium">
                        {isPhoto ? 'Publicar las fotos al subirlas' : 'Publicar al subir'}
                      </FieldLabel>
                      <FieldDescription>
                        {publishReach
                          ? `Lo verán ${publishReach}. Puedes despublicar después, pero quienes ya lo abrieron pudieron descargarlo.`
                          : 'Quedará visible para los clientes apenas termine de subir.'}
                      </FieldDescription>
                    </div>
                  </Field>
                )}
              />
            ) : null}
          </div>

          <DialogFooter className="shrink-0 border-t border-border pt-4">
            <Button
              type="button"
              variant="outline"
              disabled={pending}
              onClick={() => {
                onOpenChange(false)
              }}
            >
              Cancelar
            </Button>
            {/* Habilitado hasta que arranca la petición, y con spinner mientras dura. El rótulo
                dice el número: «Subir 8» es lo que va a pasar, «Subir» es una promesa vaga. */}
            <Button type="submit" disabled={pending}>
              {pending ? <Spinner className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
              {files.length > 1 ? `Subir ${files.length}` : 'Subir'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
